import React, { useState } from "react";
import { Box, Tabs, Tab, useTheme, useMediaQuery } from "@mui/material";
import { useDispatch } from "react-redux";
import ReferenceDetails from "./ReferenceDetails";
import CollateralDetails from "./CollateralDetails";
import { AppDispatch } from "../../../services/redux/store";
import { setStatusPopup } from "../../../services/redux/slices/statusPopupSlice";

interface ReferenceCollateralDetailsProps {
  fields: any[];
  horizontalSteps?: any[];
  renderField: (field: any) => React.ReactNode;
  formikRef: any;
  values?: any;
  isSaving?: boolean;
  pageType?: string;
  hideTabs?: boolean;
  activeTab?: number;
  onActiveTabChange?: (tabIndex: number) => void;
  activeReferenceNumber?: number;
  onActiveReferenceChange?: (referenceNumber: number) => void;
  activeCollateralNumber?: number;
  onActiveCollateralChange?: (collateralNumber: number) => void;
  onCollateralOptionsChange?: (options: {
    addressTypes: any[];
    propertyTypes: any[];
    propertySubTypes: any[];
  }) => void;
}

const ReferenceCollateralDetails: React.FC<ReferenceCollateralDetailsProps> = ({
  fields,
  horizontalSteps = [],
  renderField,
  formikRef,
  values,
  isSaving,
  hideTabs = false,
  activeTab: controlledActiveTab,
  onActiveTabChange,
  activeReferenceNumber,
  onActiveReferenceChange,
  activeCollateralNumber,
  onActiveCollateralChange,
  onCollateralOptionsChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [internalActiveTab, setInternalActiveTab] = useState(0);
  const activeTab = controlledActiveTab ?? internalActiveTab;
  const shouldHideTabs = hideTabs;

  const getNormalized = (name: any) => String(name || "").toLowerCase().replace(/[^a-z0-9]/g, "");

  // Reference: basicReference* or addressReference*
  const referenceFields = fields.filter((f: any) => {
    const n = getNormalized(f?.name);
    return n.startsWith("basicreference") || n.startsWith("addressreference");
  });

  // Collateral: hasCollateral, collateral* or coownercollateral*
  const collateralFields = fields.filter((f: any) => {
    const n = getNormalized(f?.name);
    return n === "hascollateral" || n.startsWith("collateral") || n.startsWith("coownercollateral");
  });

  const formValues = values || formikRef?.current?.values || {};

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const isOptionalReferenceAddressField = (fieldName: any) =>
    /^addressReference\d+(AddressLine2|Locality|Area)$/i.test(String(fieldName || ""));

  const getMissingReferenceFields = () => {
    const visibleReferenceFields = referenceFields.filter(
      (f: any) =>
        !f?.isRemoved &&
        f?.type !== "hidden" &&
        f?.required !== false &&
        !isOptionalReferenceAddressField(f?.name),
    );

    return visibleReferenceFields.filter(
      (f: any) => !isFilled(formValues?.[f?.name]),
    );
  };

  const handleTabChange = (_e: React.SyntheticEvent, val: number) => {
    const isMovingToCollateral = activeTab === 0 && val === 1;

    if (!isMovingToCollateral) {
      setInternalActiveTab(val);
      onActiveTabChange?.(val);
      return;
    }

    const missingReferenceFields = getMissingReferenceFields();

    if (missingReferenceFields.length > 0) {
      if (formikRef?.current?.setTouched) {
        const touched = {
          ...(formikRef?.current?.touched || {}),
        } as Record<string, boolean>;

        missingReferenceFields.forEach((f: any) => {
          if (f?.name) touched[f.name] = true;
        });

        formikRef.current.setTouched(touched, true);
      }

      dispatch(
        setStatusPopup({
          status: "error",
          message:
            "Please complete all Reference Details fields before moving to Collateral Details.",
        }),
      );
      return;
    }

    setInternalActiveTab(val);
    onActiveTabChange?.(val);
  };

  return (
    <Box
      sx={{
        width: "100%",
        pl: shouldHideTabs ? { xs: 0, sm: 2 } : 0,
        pr: shouldHideTabs ? { xs: 0, sm: 0 } : 0,
        pt: shouldHideTabs ? { xs: 1, sm: 2 } : 0,
        pb: 2,
      }}
    >
      {/* Tabs */}
      {!shouldHideTabs && (
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
            px: isXsOrSm ? 1 : 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isXsOrSm ? "fullWidth" : "standard"}
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontFamily: "Jost, sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                color: "#4B465C",
              },
              "& .Mui-selected": {
                color: "#33c2c4 !important",
                fontWeight: 600,
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#33c2c4",
              },
            }}
          >
            <Tab label="Reference Details" />
            <Tab label="Collateral Details" />
          </Tabs>
        </Box>
      )}

      {/* Tab Panels */}
      {activeTab === 0 && (
        <ReferenceDetails
          fields={referenceFields}
          renderField={renderField}
          formikRef={formikRef}
          values={values}
          horizontalSteps={horizontalSteps}
          activeReferenceNumber={activeReferenceNumber}
          onActiveReferenceChange={onActiveReferenceChange}
        />
      )}

      {activeTab === 1 && (
        <CollateralDetails
          fields={collateralFields}
          renderField={renderField}
          formikRef={formikRef}
          values={values}
          activeCollateralNumber={activeCollateralNumber}
          onActiveCollateralChange={onActiveCollateralChange}
          onCollateralOptionsChange={onCollateralOptionsChange}
        />
      )}
    </Box>
  );
};

export default ReferenceCollateralDetails;
